/**
 * The show styles list (D32): name, id, version and who wrote it; open one
 * (the show-style page), create one (`device.configure` tenant-wide). A show
 * style is the tenant's library of piece types, layers, panels and actions,
 * shared by studios. `showStyles`, `loading` and `error` are independent props.
 */
import type { ShowStyleSummaryWire } from '@acp/api-contract/showStyles.js';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { isPlainClick } from '../../lib/links.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type ShowStyleListProps = {
  readonly showStyles: readonly ShowStyleSummaryWire[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly canCreate: boolean;
  readonly onCreate: () => void;
  readonly onRetry: () => void;
  readonly onOpen: (showStyleId: string) => void;
};

export function showStyleHref(showStyleId: string): string {
  return `/show-styles/${encodeURIComponent(showStyleId)}`;
}

export function ShowStyleList({ showStyles, loading, error, canCreate, onCreate, onRetry, onOpen }: ShowStyleListProps) {
  return (
    <Card data-testid="show-style-list">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Show styles</CardTitle>
          <CardDescription>What operators play — piece types, layers, adlib panels and actions — shared by studios, which map its layers to their devices.</CardDescription>
        </div>
        {canCreate ? (
          <Button onClick={onCreate} data-testid="create-show-style">
            <PlusIcon />
            New show style
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load show styles: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && showStyles.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading show styles…</p> : null}
        {!loading && error === null && showStyles.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="show-styles-empty">
            No show styles yet.{canCreate ? ' Create one from the template to get started.' : ''}
          </p>
        ) : null}
        {showStyles.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Id</TableHead>
                <TableHead className="w-24">Version</TableHead>
                <TableHead>Last written by</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showStyles.map((s) => (
                <TableRow key={s.showStyleId} data-testid="show-style-row">
                  <TableCell className="font-medium">
                    <a
                      href={showStyleHref(s.showStyleId)}
                      className="hover:underline"
                      data-testid="open-show-style"
                      onClick={(event) => {
                        if (!isPlainClick(event)) return;
                        event.preventDefault();
                        onOpen(s.showStyleId);
                      }}
                    >
                      {s.name}
                    </a>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{s.showStyleId}</TableCell>
                  <TableCell className="font-mono text-sm">v{s.version}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.updatedBy}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
